import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import ReactSelect from "react-select";
import {
  usuarioCreateSchema,
  usuarioUpdateSchema,
  UsuarioCreateData,
  UsuarioUpdateData,
} from "../../../lib/validations";
import FormInput from "../../../components/FormInput";
import CustomSwitch from "../../../components/Switch";
import FormActions from "../../../components/Form/FormActions";
import { optionsService } from "../../../services/optionsService";
import { controleProducaoService, SetorProducao } from "../../../services/controleProducaoService";
import { COLORS, SHADOWS } from "../../../constants";
import { useAuth } from "../../../context/AuthContext";
import { isDesenvolvedorRoleNome } from "../../../lib/screenAccess";

type UsuarioFormData = UsuarioCreateData | UsuarioUpdateData;

interface UsuarioFormProps {
  initialData?: UsuarioUpdateData;
  onSubmit: (data: UsuarioFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const UsuarioForm = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading,
}: UsuarioFormProps) => {
  const { user } = useAuth();
  const isDesenvolvedor = isDesenvolvedorRoleNome(user?.role?.nome);
  const [lojas, setLojas] = useState<Array<{ id: number; nome: string }>>([]);
  const [roles, setRoles] = useState<Array<{ id: number; nome: string }>>([]);
  const [setores, setSetores] = useState<SetorProducao[]>([]);

  const [produzPorciona, setProduzPorciona] = useState(
    () => (initialData?.setorIds ?? []).length > 0
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<UsuarioFormData>({
    resolver: zodResolver(
      initialData ? usuarioUpdateSchema : usuarioCreateSchema
    ),
    defaultValues: initialData
      ? {
          ...initialData,
          senha: "",
          isConferenteBase: initialData.isConferenteBase ?? false,
          isSupervisorProducao: initialData.isSupervisorProducao ?? false,
          setorIds: initialData.setorIds ?? [],
          ativo: initialData.ativo ?? true,
        }
      : {
          nome: "",
          email: "",
          senha: "",
          isConferenteBase: false,
          isSupervisorProducao: false,
          setorIds: [],
          lojaId: undefined,
          roleId: undefined,
        },
  });

  const selectedLojaId = watch("lojaId");

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [lojasData, rolesData] = await Promise.all([
          optionsService.getLojas(),
          optionsService.getRoles(
            selectedLojaId ? Number(selectedLojaId) : undefined,
            isDesenvolvedor
          ),
        ]);
        setLojas(lojasData || []);
        setRoles(rolesData || []);
      } catch (error) {
        console.error("Erro ao buscar opções:", error);
      }
    };
    fetchOptions();
  }, [selectedLojaId, isDesenvolvedor]);

  useEffect(() => {
    if (!selectedLojaId) {
      setSetores([]);
      return;
    }
    void controleProducaoService
      .listSetores(Number(selectedLojaId))
      .then(setSetores)
      .catch(() => setSetores([]));
  }, [selectedLojaId]);

  useEffect(() => {
    if (initialData) {
      reset({
        ...initialData,
        senha: "",
        isConferenteBase: initialData.isConferenteBase ?? false,
        isSupervisorProducao: initialData.isSupervisorProducao ?? false,
        setorIds: initialData.setorIds ?? [],
        ativo: initialData.ativo ?? true,
      });
      setProduzPorciona((initialData.setorIds ?? []).length > 0);
    } else {
      reset({
        nome: "",
        email: "",
        senha: "",
        isConferenteBase: false,
        isSupervisorProducao: false,
        setorIds: [],
        lojaId: undefined,
        roleId: undefined,
      });
      setProduzPorciona(false);
    }
  }, [initialData, reset]);

  const handleProduzPorcionaChange = (checked: boolean) => {
    setProduzPorciona(checked);
    if (!checked) {
      setValue("setorIds", []);
      clearErrors("setorIds");
    }
  };

  const handleFormSubmit = (data: UsuarioFormData) => {
    const setorIds = produzPorciona ? data.setorIds ?? [] : [];
    if (produzPorciona && setorIds.length === 0) {
      setError("setorIds", {
        type: "manual",
        message: "Selecione pelo menos um setor.",
      });
      return;
    }
    onSubmit({ ...data, setorIds });
  };

  const lojaOptions = lojas.map((loja) => ({
    value: loja.id,
    label: loja.nome,
  }));

  const roleOptions = roles.map((role) => ({
    value: role.id,
    label: role.nome,
  }));

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {initialData && (
        <Controller
          name="ativo"
          control={control}
          defaultValue={true}
          render={({ field }) => (
            <CustomSwitch
              label="Ativo?"
              checked={field.value ?? true}
              onChange={field.onChange}
              error={(errors as any).ativo?.message}
            />
          )}
        />
      )}

      <FormInput
        label="Nome"
        registration={register("nome")}
        error={errors.nome?.message}
      />

      <FormInput
        label="Email"
        type="email"
        registration={register("email")}
        error={errors.email?.message}
      />

      <FormInput
        label="Senha"
        type="password"
        registration={register("senha" as keyof UsuarioFormData)}
        error={(errors as any).senha?.message}
        placeholder={initialData ? "Deixe em branco para não alterar" : ""}
      />

      <div>
        <label className="mb-1 block text-sm font-medium text-neutral-700">
          Loja
        </label>
        <Controller
          name="lojaId"
          control={control}
          render={({ field }) => (
            <ReactSelect
              {...field}
              options={lojaOptions}
              placeholder="Selecione uma loja"
              isClearable
              value={
                lojaOptions.find((option) => option.value === field.value) ||
                null
              }
              onChange={(option) => field.onChange(option?.value || undefined)}
              styles={{
                control: (base) => ({
                  ...base,
                  borderColor: errors.lojaId
                    ? COLORS.ERROR
                    : COLORS.NEUTRAL_300,
                  "&:hover": {
                    borderColor: errors.lojaId ? COLORS.ERROR : COLORS.PRIMARY,
                  },
                  "&:focus-within": {
                    borderColor: COLORS.PRIMARY,
                    boxShadow: SHADOWS.selectFocusPrimary,
                  },
                }),
              }}
            />
          )}
        />
        {errors.lojaId && (
          <p className="mt-1 text-sm text-red-600">{errors.lojaId.message}</p>
        )}
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-neutral-700">
          Cargo
        </label>
        <Controller
          name="roleId"
          control={control}
          render={({ field }) => (
            <ReactSelect
              {...field}
              options={roleOptions}
              placeholder="Selecione um cargo"
              isClearable
              value={
                roleOptions.find((option) => option.value === field.value) ||
                null
              }
              onChange={(option) => field.onChange(option?.value || undefined)}
              styles={{
                control: (base) => ({
                  ...base,
                  borderColor: errors.roleId
                    ? COLORS.ERROR
                    : COLORS.NEUTRAL_300,
                  "&:hover": {
                    borderColor: errors.roleId ? COLORS.ERROR : COLORS.PRIMARY,
                  },
                  "&:focus-within": {
                    borderColor: COLORS.PRIMARY,
                    boxShadow: SHADOWS.selectFocusPrimary,
                  },
                }),
              }}
            />
          )}
        />
        {errors.roleId && (
          <p className="mt-1 text-sm text-red-600">{errors.roleId.message}</p>
        )}
      </div>

      <Controller
        name="isConferenteBase"
        control={control}
        defaultValue={false}
        render={({ field }) => (
          <CustomSwitch
            label="O usuário é conferente base?"
            checked={field.value ?? false}
            onChange={field.onChange}
            error={(errors as any).isConferenteBase?.message}
          />
        )}
      />

      <Controller
        name="isSupervisorProducao"
        control={control}
        defaultValue={false}
        render={({ field }) => (
          <CustomSwitch
            label="O usuário é supervisor de produção? Assina a produção total dos setores."
            checked={field.value ?? false}
            onChange={field.onChange}
            error={(errors as any).isSupervisorProducao?.message}
          />
        )}
      />

      <CustomSwitch
        label="O usuário produz (porciona)?"
        checked={produzPorciona}
        onChange={handleProduzPorcionaChange}
      />

      {produzPorciona && (
        <Controller
          name="setorIds"
          control={control}
          defaultValue={[]}
          render={({ field }) => (
            <div>
              <p className="mb-2 text-sm font-medium text-neutral-700">
                Setores de produção
              </p>
              {setores.length === 0 ? (
                <p className="text-xs text-neutral-500">
                  Selecione a loja para listar os setores.
                </p>
              ) : (
                <div className="space-y-2 rounded-md border border-neutral-200 p-3">
                  {setores.map((setor) => {
                    const checked = (field.value ?? []).includes(setor.id);
                    return (
                      <label
                        key={setor.id}
                        className="flex items-center gap-2 text-sm text-neutral-700"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const current = field.value ?? [];
                            const next = e.target.checked
                              ? [...current, setor.id]
                              : current.filter((id: number) => id !== setor.id);
                            field.onChange(next);
                            if (next.length > 0) {
                              clearErrors("setorIds");
                            }
                          }}
                        />
                        {setor.nome}
                      </label>
                    );
                  })}
                </div>
              )}
              {(errors as any).setorIds?.message && (
                <p className="mt-1 text-sm text-red-600">
                  {(errors as any).setorIds.message}
                </p>
              )}
            </div>
          )}
        />
      )}

      <FormActions onCancel={onCancel} isLoading={isLoading} />
    </form>
  );
};

export default UsuarioForm;
